import { useState } from "react";

const FORM_ID = "daimqfboh653ivfvogog";
const SUBMIT_ADDR = "https://readdy.ai/api/form/daimqfboh653ivfvogog";

type Status = "idle" | "loading" | "success" | "error";

export default function SubscribeCta() {
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState("");
  const [done, setDone] = useState("");

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;

    // read payload from the real DOM form BEFORE any async work
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      setDone("订阅成功，我们会把新的解析报告推送到你的邮箱。");
      return;
    }

    formData.delete("website_alt");

    const body = new URLSearchParams();
    formData.forEach((value, key) => {
      if (typeof value === "string") body.append(key, value);
    });

    setStatus("loading");
    setFormError("");

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });

      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null =
        null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";

      const looksSpam = /spam/i.test(String(serverMsg));
      const ok = response.ok && parsed?.code === "OK" && !looksSpam;

      if (ok) {
        setStatus("success");
        setDone("订阅成功，我们会把新的解析报告推送到你的邮箱。");
        form.reset();
      } else {
        setStatus("error");
        setFormError(serverMsg || "提交失败，请稍后重试。");
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，提交失败，请稍后重试。");
    }
  };

  return (
    <section id="subscribe" className="bg-background-50 py-14 md:py-20">
      <div className="mx-auto w-full max-w-site px-4 md:px-8">
        <div className="overflow-hidden rounded-lg border border-background-200 bg-foreground-950">
          <div className="grid grid-cols-1 lg:grid-cols-12">
            <div className="p-7 md:p-10 lg:col-span-7">
              <span className="inline-flex items-center gap-2 text-xs font-medium tracking-[0.24em] text-accent-400">
                <i className="ri-notification-3-line text-sm"></i>
                SUBSCRIBE
              </span>
              <h2 className="mt-3 font-heading text-2xl font-bold text-background-50 md:text-3xl">
                订阅解析更新
              </h2>
              <p className="mt-4 max-w-lg text-sm leading-7 text-background-200/80">
                新的黑料深度解析、动机分析与关联人物图谱更新后，我们会第一时间通知你。只推送有完整脉络的内容，不转发热度泡沫。
              </p>

              <ul className="mt-6 flex flex-col gap-3">
                {["每周一次的深度解析摘要", "重大舆情反转的即时提醒", "可随时退订，不打扰"].map((t) => (
                  <li key={t} className="flex items-center gap-2.5 text-sm text-background-200/85">
                    <i className="ri-check-line text-base text-accent-400"></i>
                    {t}
                  </li>
                ))}
              </ul>
            </div>

            <div className="border-t border-background-100/10 p-7 md:p-10 lg:col-span-5 lg:border-l lg:border-t-0">
              <form id={FORM_ID} data-readdy-form onSubmit={handleSubmit} className="flex flex-col gap-4">
                <div>
                  <label htmlFor="sub-email" className="text-xs font-medium text-background-200/80">
                    邮箱地址
                  </label>
                  <input
                    id="sub-email"
                    name="email"
                    type="email"
                    required
                    placeholder="you@example.com"
                    className="mt-2 h-11 w-full rounded-md border border-background-100/20 bg-background-50/10 px-3.5 text-sm text-background-50 placeholder:text-background-100/45 focus:border-accent-400 focus:outline-none"
                  />
                </div>

                <div>
                  <label htmlFor="sub-focus" className="text-xs font-medium text-background-200/80">
                    关注方向
                  </label>
                  <select
                    id="sub-focus"
                    name="focus"
                    defaultValue="全部解析"
                    className="mt-2 h-11 w-full cursor-pointer rounded-md border border-background-100/20 bg-background-50/10 px-3.5 text-sm text-background-50 focus:border-accent-400 focus:outline-none"
                  >
                    <option className="text-foreground-950" value="全部解析">
                      全部解析
                    </option>
                    <option className="text-foreground-950" value="真相还原">
                      真相还原
                    </option>
                    <option className="text-foreground-950" value="动机分析">
                      动机分析
                    </option>
                    <option className="text-foreground-950" value="利益链">
                      利益链
                    </option>
                    <option className="text-foreground-950" value="关联人物">
                      关联人物
                    </option>
                  </select>
                </div>

                {/* anti-spam honeypot */}
                <div className="hp-field-17" aria-hidden="true">
                  <label htmlFor="sub-website-alt">请勿填写</label>
                  <input
                    id="sub-website-alt"
                    name="website_alt"
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    readOnly
                  />
                </div>

                <button
                  type="submit"
                  disabled={status === "loading"}
                  className="mt-1 inline-flex h-11 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-70"
                >
                  {status === "loading" ? (
                    <>
                      <i className="ri-loader-4-line animate-spin text-base"></i>
                      提交中…
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-line text-base"></i>
                      立即订阅
                    </>
                  )}
                </button>

                {status === "success" && (
                  <p className="flex items-center gap-2 rounded-md bg-accent-500/15 px-3.5 py-2.5 text-xs text-accent-300">
                    <i className="ri-checkbox-circle-line text-base"></i>
                    {done}
                  </p>
                )}
                {status === "error" && (
                  <p className="flex items-center gap-2 rounded-md bg-primary-500/15 px-3.5 py-2.5 text-xs text-primary-300">
                    <i className="ri-error-warning-line text-base"></i>
                    {formError}
                  </p>
                )}
                <p className="text-[11px] leading-5 text-background-100/45">
                  提交即表示你同意接收站点更新邮件，我们不会将你的邮箱用于其他用途。
                </p>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}